import { useSyncExternalStore } from 'react'

/**
 * Whether, and how, to offer the installed app (the install notice).
 *
 * Chromium browsers fire `beforeinstallprompt` when the app can be installed; the event is held
 * so the notice's button can open the browser's own dialog, and its mini-infobar is suppressed in
 * favour of the notice. Safari and Firefox fire nothing: on a phone the notice says where the
 * browser keeps its "Add to Home Screen", and on a computer it is not offered. Running installed,
 * or dismissed in the last week, nothing is offered.
 */

/** The offer: the browser's own dialog, Safari's Share menu, another phone browser's menu. */
export type InstallOffer = 'prompt' | 'ios' | 'menu' | null

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED_KEY = 'damis.install.dismissed_at'
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000

let deferred: InstallPromptEvent | null = null
let installed = false
/** Dismissed with storage refused (private browsing): for this page only. */
let snoozed = false
const listeners = new Set<() => void>()

function notify() {
  for (const listener of listeners) listener()
}

// At import, before React mounts: Chrome can fire the event as soon as the page has loaded.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as InstallPromptEvent
    notify()
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    installed = true
    notify()
  })
}

function matches(query: string): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches
}

/** Opened from the home screen rather than a browser tab. */
export function isStandalone(): boolean {
  return (
    matches('(display-mode: standalone)') ||
    ('standalone' in navigator && navigator.standalone === true)
  )
}

function isIos(): boolean {
  // iPadOS asks for the desktop site and calls itself a Mac; a Mac has no touch screen.
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

function isPhoneOrTablet(): boolean {
  return isIos() || /Android|Mobi/i.test(navigator.userAgent)
}

function dismissedRecently(now: number): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISSED_KEY))
    return Number.isFinite(at) && now - at < SNOOZE_MS
  } catch {
    return false
  }
}

export function installOffer(now = Date.now()): InstallOffer {
  if (installed || snoozed || isStandalone() || dismissedRecently(now)) return null
  if (deferred) return 'prompt'
  if (isIos()) return 'ios'
  if (isPhoneOrTablet()) return 'menu'
  return null
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useInstallOffer(): InstallOffer {
  return useSyncExternalStore(subscribe, () => installOffer())
}

/** Open the browser's install dialog. Declined there, the notice is snoozed like "Not now". */
export async function install(): Promise<void> {
  const event = deferred
  if (!event) return
  deferred = null
  await event.prompt()
  const { outcome } = await event.userChoice
  if (outcome === 'dismissed') dismissInstall()
  else notify()
}

/** "Not now": the notice stays away for a week. */
export function dismissInstall(now = Date.now()): void {
  try {
    localStorage.setItem(DISMISSED_KEY, String(now))
  } catch {
    snoozed = true
  }
  notify()
}

/** Forget a held event, an install and a snooze: each test starts from a fresh page. */
export function resetInstallForTests(): void {
  deferred = null
  installed = false
  snoozed = false
  notify()
}
